/*
 * Affiliate — Creator Partner application form.
 * Posts to the pemmican-outpost app's public /apply endpoint.
 *
 * Server contract: /docs/partner-program-frontend-brief.md (in the outpost repo).
 * Theme does client-side validation mirroring the server rules, POSTs JSON,
 * renders inline errors for 400/429/502, and swaps to a success card on 200.
 */

(function () {
  'use strict';

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var APPLICATION_ANSWER_MAX = 600;

  function init(root) {
    var form = root.querySelector('[data-af-apply-form]');
    if (!form || form.dataset.afBound === '1') return;
    form.dataset.afBound = '1';

    var endpoint = root.dataset.endpoint;
    if (!endpoint) {
      console.error('[affiliate-apply] missing data-endpoint on section root');
      return;
    }

    var submit = form.querySelector('[data-af-submit]');
    var formError = form.querySelector('[data-af-form-error]');
    var counter = form.querySelector('[data-af-counter]');
    var answerField = form.querySelector('[name="application_answer"]');
    var successEl = root.querySelector('[data-af-success]');
    var successBody = root.querySelector('[data-af-success-body]');
    var successTemplate = (successEl && successEl.dataset.template) || 'Thanks, [first_name].';

    if (answerField && counter) {
      var updateCounter = function () {
        var len = answerField.value.length;
        counter.textContent = len + ' / ' + APPLICATION_ANSWER_MAX;
        counter.classList.toggle('pc-apply__counter--over', len > APPLICATION_ANSWER_MAX);
      };
      answerField.addEventListener('input', updateCounter);
      updateCounter();
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      clearErrors(form, formError);

      var payload = collectPayload(form);
      var clientErrors = validate(payload);
      if (clientErrors.length) {
        applyFieldErrors(form, clientErrors);
        return;
      }

      setSubmitting(form, submit, true);

      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (res) {
          return res.json().then(function (body) { return { status: res.status, body: body }; });
        })
        .then(function (result) {
          setSubmitting(form, submit, false);

          if (result.status === 200) {
            renderSuccess(root, successEl, successBody, successTemplate, result.body);
            return;
          }

          handleErrorResponse(form, formError, result.status, result.body);
        })
        .catch(function (err) {
          console.error('[affiliate-apply] network error', err);
          setSubmitting(form, submit, false);
          showFormError(formError, 'We couldn’t reach our server. Check your connection and try again, or drop us a note at contact@pemmicanproject.com.');
        });
    });
  }

  function collectPayload(form) {
    var data = new FormData(form);
    var consent = form.querySelector('[name="content_consent"]');

    var payload = {
      name: trim(data.get('name')),
      email: trim(data.get('email')),
      instagram_handle: stripAt(data.get('instagram_handle')),
      tiktok_handle: stripAt(data.get('tiktok_handle')),
      audience_location: trim(data.get('audience_location')),
      application_answer: trim(data.get('application_answer')),
      content_consent: !!(consent && consent.checked)
    };

    // Shipping address — sent to the backend which forwards to Shopify
    // customerCreate so the trial pack draft order can use the customer's
    // default address.
    var shipping = {
      phone: trim(data.get('phone')) || null,
      address1: trim(data.get('address1')),
      address2: trim(data.get('address2')) || null,
      city: trim(data.get('city')),
      province: trim(data.get('province')) || null,
      postal_code: trim(data.get('postal_code')),
      country_code: trim(data.get('country_code')) || 'GB'
    };
    payload.shipping_address = shipping;

    // Drop empty optional strings to keep the payload tidy
    ['instagram_handle', 'tiktok_handle', 'audience_location', 'application_answer'].forEach(function (k) {
      if (!payload[k]) delete payload[k];
    });

    return payload;
  }

  function validate(p) {
    var errors = [];
    if (!p.name) errors.push({ field: 'name', message: 'Please tell us your name.' });
    if (!p.email) {
      errors.push({ field: 'email', message: 'Email is required.' });
    } else if (!EMAIL_RE.test(p.email)) {
      errors.push({ field: 'email', message: 'That email doesn’t look right.' });
    }
    if (!p.instagram_handle && !p.tiktok_handle) {
      errors.push({ field: 'instagram_handle', message: 'Add an Instagram or TikTok handle so we can take a look.' });
    }
    if (p.content_consent !== true) {
      errors.push({ field: 'content_consent', message: 'We need your consent to run your content as ads.' });
    }
    var addr = p.shipping_address || {};
    if (!addr.address1) errors.push({ field: 'address1', message: 'Address line 1 is required.' });
    if (!addr.city) errors.push({ field: 'city', message: 'City is required.' });
    if (!addr.postal_code) errors.push({ field: 'postal_code', message: 'Postcode is required.' });
    if (!addr.country_code) errors.push({ field: 'country_code', message: 'Country is required.' });
    return errors;
  }

  function handleErrorResponse(form, formError, status, body) {
    var detail = (body && body.detail) || '';

    if (status === 429) {
      showFormError(formError, 'We’re getting a lot of applications right now. Give it a minute and try again.');
      return;
    }

    if (status === 400) {
      // Map known server error messages to specific fields
      if (/name|email/i.test(detail) && (/required/i.test(detail))) {
        applyFieldErrors(form, [
          { field: 'name', message: 'Please tell us your name.' },
          { field: 'email', message: 'Email is required.' }
        ]);
        return;
      }
      if (/social handle/i.test(detail)) {
        applyFieldErrors(form, [{ field: 'instagram_handle', message: 'Add an Instagram or TikTok handle.' }]);
        return;
      }
      if (/consent/i.test(detail)) {
        applyFieldErrors(form, [{ field: 'content_consent', message: detail }]);
        return;
      }
      showFormError(formError, detail || 'Something in the form didn’t pass our checks.');
      return;
    }

    // 502 / 5xx / unknown
    showFormError(formError, 'Something went wrong on our end. Try again in a moment, or email contact@pemmicanproject.com.');
  }

  function setSubmitting(form, submit, isSubmitting) {
    form.classList.toggle('is-submitting', isSubmitting);
    if (submit) submit.disabled = isSubmitting;
  }

  function clearErrors(form, formError) {
    form.querySelectorAll('.pc-apply__field.has-error').forEach(function (f) {
      f.classList.remove('has-error');
    });
    if (formError) {
      formError.classList.remove('is-visible');
      formError.textContent = '';
    }
  }

  function applyFieldErrors(form, errors) {
    errors.forEach(function (err) {
      var input = form.querySelector('[name="' + err.field + '"]');
      if (!input) return;
      var wrap = input.closest('.pc-apply__field') || input.closest('.pc-apply__consent');
      if (!wrap) return;
      wrap.classList.add('has-error');
      var msgEl = wrap.querySelector('.pc-apply__error');
      if (msgEl && err.message) msgEl.textContent = err.message;
    });

    // Scroll the first errored field into view
    var firstErrored = form.querySelector('.has-error');
    if (firstErrored && typeof firstErrored.scrollIntoView === 'function') {
      firstErrored.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  function showFormError(el, message) {
    if (!el) return;
    el.textContent = message;
    el.classList.add('is-visible');
    if (typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  function renderSuccess(root, successEl, successBody, template, affiliate) {
    var firstName = ((affiliate && affiliate.name) || '').trim().split(/\s+/)[0] || 'there';
    if (successBody) {
      successBody.textContent = template.replace(/\[first_name\]/g, firstName);
    }
    if (successEl) successEl.classList.add('is-visible');
    root.classList.add('is-success');

    // Bring the success card into view
    if (typeof root.scrollIntoView === 'function') {
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  function trim(v) {
    return (v == null ? '' : String(v)).trim();
  }

  function stripAt(v) {
    var s = trim(v);
    return s.replace(/^@+/, '');
  }

  // Init all instances of the section (supports Shopify section reload in editor)
  function initAll() {
    document.querySelectorAll('[data-af-apply]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init when Shopify theme editor reloads the section
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target) e.target.querySelectorAll('[data-af-apply]').forEach(init);
  });
})();
